import { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Button,
  Flex,
  Input,
  Text,
  Textarea,
  Grid,
  GridItem,
  Table,
  IconButton,
  Badge,
  DialogRoot,
  DialogBackdrop,
  DialogPositioner,
  DialogContent,
  DialogCloseTrigger,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@chakra-ui/react'
import { Plus, Trash2, AlertCircle, Check } from 'lucide-react'
import { Field } from '@/components/ui/field'
import { NativeSelectRoot, NativeSelectField } from '@/components/ui/native-select'
import {
  useCreateSupplierInvoice,
  useSuppliers,
  useSupplierActiveContracts,
  useResidenceChartAccounts,
} from '../hooks'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { accountingToaster as toaster } from '../pages/AccountingLayout'
import type { ResidenceChartAccount } from '../types'

interface SupplierInvoiceModalProps {
  isOpen: boolean
  onClose: () => void
  residenceId: string
  fiscalYearId: string
}

interface InvoiceLine {
  id: string
  description: string
  account_id: string
  budget_id: string
  distribution_key_id: string
  quantity: number
  unit_price_ht: number
  vat_rate: number
}

type FormData = {
  supplier_id: string
  contract_id: string
  invoice_number: string
  invoice_date: string
  due_date: string
  receipt_date: string
  accounting_date: string
  description: string
  notes: string
}

const getToday = (): string => new Date().toISOString().split('T')[0] as string

const initialFormData: FormData = {
  supplier_id: '',
  contract_id: '',
  invoice_number: '',
  invoice_date: getToday(),
  due_date: getToday(),
  receipt_date: getToday(),
  accounting_date: getToday(),
  description: '',
  notes: '',
}

const createEmptyLine = (): InvoiceLine => ({
  id: crypto.randomUUID(),
  description: '',
  account_id: '',
  budget_id: '',
  distribution_key_id: '',
  quantity: 1,
  unit_price_ht: 0,
  vat_rate: 20,
})

export function SupplierInvoiceModal({
  isOpen,
  onClose,
  residenceId,
  fiscalYearId,
}: SupplierInvoiceModalProps) {
  const { data: authData } = useAuth()
  const createInvoice = useCreateSupplierInvoice()

  const { data: suppliers } = useSuppliers({
    agency_id: authData?.profile?.agency_id || '',
    is_active: true,
  })

  const [formData, setFormData] = useState<FormData>(initialFormData)
  const [lines, setLines] = useState<InvoiceLine[]>([createEmptyLine()])

  // Charger les contrats actifs du fournisseur sélectionné
  const { data: contracts } = useSupplierActiveContracts(
    residenceId,
    formData.supplier_id || undefined
  )

  const { data: residenceAccounts } = useResidenceChartAccounts(residenceId)

  // Filtrer les comptes de charge (classe 6)
  const chargeAccounts = residenceAccounts?.filter(
    (a) => a.chart_account?.code?.startsWith('6') && a.is_active
  )

  // Calculer les totaux
  const totals = useMemo(() => {
    return lines.reduce(
      (acc, line) => {
        const lineHt = line.quantity * line.unit_price_ht
        const lineVat = lineHt * (line.vat_rate / 100)
        return {
          ht: acc.ht + lineHt,
          vat: acc.vat + lineVat,
          ttc: acc.ttc + lineHt + lineVat,
        }
      },
      { ht: 0, vat: 0, ttc: 0 }
    )
  }, [lines])

  const hasValidLines = lines.some((l) => l.account_id && l.unit_price_ht > 0)

  // Quand le fournisseur change, calculer la date d'échéance
  useEffect(() => {
    if (formData.supplier_id && formData.invoice_date) {
      const supplier = suppliers?.find((s) => s.id === formData.supplier_id)
      if (supplier) {
        const invoiceDate = new Date(formData.invoice_date)
        invoiceDate.setDate(invoiceDate.getDate() + (supplier.payment_delay || 30))
        const newDueDate = invoiceDate.toISOString().split('T')[0] as string
        setFormData((prev) => ({
          ...prev,
          due_date: newDueDate,
        }))
      }
    }
  }, [formData.supplier_id, formData.invoice_date, suppliers])

  // Quand un contrat est sélectionné, pré-remplir la ligne
  useEffect(() => {
    if (formData.contract_id && contracts) {
      const contract = contracts.find((c) => c.id === formData.contract_id)
      if (contract) {
        setLines([
          {
            id: crypto.randomUUID(),
            description: contract.label,
            account_id: contract.default_account_id || '',
            budget_id: contract.default_budget_id || '',
            distribution_key_id: '',
            quantity: 1,
            unit_price_ht: 0,
            vat_rate: 20,
          },
        ])
      }
    }
  }, [formData.contract_id, contracts])

  const handleClose = () => {
    setFormData(initialFormData)
    setLines([createEmptyLine()])
    onClose()
  }

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    // Reset contract when supplier changes
    if (field === 'supplier_id') {
      setFormData((prev) => ({ ...prev, contract_id: '' }))
    }
  }

  const handleLineChange = (id: string, field: keyof InvoiceLine, value: string | number) => {
    setLines((prev) =>
      prev.map((l) =>
        l.id === id
          ? {
              ...l,
              [field]:
                field === 'quantity' || field === 'unit_price_ht' || field === 'vat_rate'
                  ? Number(value) || 0
                  : value,
            }
          : l
      )
    )
  }

  const handleAddLine = () => {
    setLines((prev) => [...prev, createEmptyLine()])
  }

  const handleRemoveLine = (id: string) => {
    if (lines.length <= 1) {
      toaster.create({
        title: 'Minimum 1 ligne',
        description: 'Une facture doit avoir au minimum 1 ligne',
        type: 'warning',
      })
      return
    }
    setLines((prev) => prev.filter((l) => l.id !== id))
  }

  const handleSubmit = async () => {
    if (!formData.supplier_id) {
      toaster.create({
        title: 'Erreur',
        description: 'Le fournisseur est obligatoire',
        type: 'error',
      })
      return
    }

    if (!formData.invoice_number.trim()) {
      toaster.create({
        title: 'Erreur',
        description: 'Le numéro de facture est obligatoire',
        type: 'error',
      })
      return
    }

    if (!hasValidLines) {
      toaster.create({
        title: 'Erreur',
        description: 'Veuillez saisir au moins une ligne avec un compte et un montant',
        type: 'error',
      })
      return
    }

    try {
      const validLines = lines
        .filter((l) => l.account_id && l.unit_price_ht > 0)
        .map((l) => {
          const amountHt = l.quantity * l.unit_price_ht
          const vatAmount = amountHt * (l.vat_rate / 100)
          return {
            description: l.description || 'Ligne de facture',
            account_id: l.account_id,
            budget_id: l.budget_id || null,
            distribution_key_id: l.distribution_key_id || null,
            quantity: l.quantity,
            unit_price_ht: l.unit_price_ht,
            amount_ht: Math.round(amountHt * 100) / 100,
            vat_rate: l.vat_rate,
            vat_amount: Math.round(vatAmount * 100) / 100,
            amount_ttc: Math.round((amountHt + vatAmount) * 100) / 100,
          }
        })

      await createInvoice.mutateAsync({
        invoice: {
          residence_id: residenceId,
          fiscal_year_id: fiscalYearId,
          supplier_id: formData.supplier_id,
          contract_id: formData.contract_id || null,
          invoice_number: formData.invoice_number.trim(),
          invoice_date: formData.invoice_date,
          due_date: formData.due_date,
          receipt_date: formData.receipt_date || null,
          accounting_date: formData.accounting_date,
          description: formData.description.trim() || null,
          notes: formData.notes.trim() || null,
          total_ht: Math.round(totals.ht * 100) / 100,
          total_vat: Math.round(totals.vat * 100) / 100,
          total_ttc: Math.round(totals.ttc * 100) / 100,
        },
        lines: validLines,
      })

      toaster.create({
        title: 'Succès',
        description: 'Facture créée avec succès',
        type: 'success',
      })

      handleClose()
    } catch (error) {
      toaster.create({
        title: 'Erreur',
        description: error instanceof Error ? error.message : "Erreur lors de l'enregistrement",
        type: 'error',
      })
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(value)
  }

  // Grouper les comptes par classe
  const groupedAccounts = useMemo(() => {
    if (!chargeAccounts) return {}
    const groups: Record<string, ResidenceChartAccount[]> = {}
    chargeAccounts.forEach((account) => {
      if (!account.chart_account) return
      const classNum = account.chart_account.code.substring(0, 2)
      if (!groups[classNum]) groups[classNum] = []
      groups[classNum].push(account)
    })
    return groups
  }, [chargeAccounts])

  return (
    <DialogRoot open={isOpen} onOpenChange={(e) => !e.open && handleClose()} size="xl">
      <DialogBackdrop />
      <DialogPositioner>
        <DialogContent maxW="1000px">
          <DialogCloseTrigger />
          <DialogHeader>
            <DialogTitle>Nouvelle facture fournisseur</DialogTitle>
          </DialogHeader>
          <DialogBody>
            {/* Informations générales */}
            <Grid templateColumns="repeat(3, 1fr)" gap={4} mb={6}>
              <GridItem colSpan={2}>
                <Field label="Fournisseur" required>
                  <NativeSelectRoot>
                    <NativeSelectField
                      value={formData.supplier_id}
                      onChange={(e) => handleChange('supplier_id', e.target.value)}
                    >
                      <option value="">Sélectionner un fournisseur</option>
                      {suppliers?.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.code} - {s.name}
                        </option>
                      ))}
                    </NativeSelectField>
                  </NativeSelectRoot>
                </Field>
              </GridItem>

              <GridItem>
                <Field label="Contrat associé">
                  <NativeSelectRoot>
                    <NativeSelectField
                      value={formData.contract_id}
                      onChange={(e) => handleChange('contract_id', e.target.value)}
                      disabled={!formData.supplier_id || !contracts?.length}
                    >
                      <option value="">Aucun contrat</option>
                      {contracts?.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.reference} - {c.label}
                        </option>
                      ))}
                    </NativeSelectField>
                  </NativeSelectRoot>
                </Field>
              </GridItem>

              <GridItem>
                <Field label="N° Facture fournisseur" required>
                  <Input
                    value={formData.invoice_number}
                    onChange={(e) => handleChange('invoice_number', e.target.value)}
                    placeholder="FA-2024-001"
                  />
                </Field>
              </GridItem>

              <GridItem>
                <Field label="Date facture" required>
                  <Input
                    type="date"
                    value={formData.invoice_date}
                    onChange={(e) => handleChange('invoice_date', e.target.value)}
                  />
                </Field>
              </GridItem>

              <GridItem>
                <Field label="Date d'échéance">
                  <Input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => handleChange('due_date', e.target.value)}
                  />
                </Field>
              </GridItem>

              <GridItem>
                <Field label="Date de réception">
                  <Input
                    type="date"
                    value={formData.receipt_date}
                    onChange={(e) => handleChange('receipt_date', e.target.value)}
                  />
                </Field>
              </GridItem>

              <GridItem>
                <Field label="Date comptable">
                  <Input
                    type="date"
                    value={formData.accounting_date}
                    onChange={(e) => handleChange('accounting_date', e.target.value)}
                  />
                </Field>
              </GridItem>

              <GridItem>
                <Field label="Description">
                  <Input
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    placeholder="Objet de la facture"
                  />
                </Field>
              </GridItem>
            </Grid>

            {/* Lignes de facture */}
            <Box borderWidth="1px" borderRadius="md" overflow="hidden" mb={4}>
              <Table.Root size="sm">
                <Table.Header>
                  <Table.Row bg="gray.50">
                    <Table.ColumnHeader w="25%">Compte</Table.ColumnHeader>
                    <Table.ColumnHeader>Description</Table.ColumnHeader>
                    <Table.ColumnHeader w="80px" textAlign="right">
                      Qté
                    </Table.ColumnHeader>
                    <Table.ColumnHeader w="100px" textAlign="right">
                      P.U. HT
                    </Table.ColumnHeader>
                    <Table.ColumnHeader w="70px" textAlign="right">
                      TVA %
                    </Table.ColumnHeader>
                    <Table.ColumnHeader w="100px" textAlign="right">
                      Total TTC
                    </Table.ColumnHeader>
                    <Table.ColumnHeader w="40px"></Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {lines.map((line) => {
                    const lineHt = line.quantity * line.unit_price_ht
                    const lineVat = lineHt * (line.vat_rate / 100)
                    const lineTtc = lineHt + lineVat

                    return (
                      <Table.Row key={line.id}>
                        <Table.Cell>
                          <NativeSelectRoot size="sm">
                            <NativeSelectField
                              value={line.account_id}
                              onChange={(e) => handleLineChange(line.id, 'account_id', e.target.value)}
                            >
                              <option value="">Compte...</option>
                              {Object.entries(groupedAccounts).map(([classNum, accounts]) => (
                                <optgroup key={classNum} label={`Classe ${classNum}`}>
                                  {accounts.map((account) => (
                                    <option key={account.id} value={account.id}>
                                      {account.custom_code || account.chart_account?.code} -{' '}
                                      {account.custom_name || account.chart_account?.name}
                                    </option>
                                  ))}
                                </optgroup>
                              ))}
                            </NativeSelectField>
                          </NativeSelectRoot>
                        </Table.Cell>
                        <Table.Cell>
                          <Input
                            size="sm"
                            value={line.description}
                            onChange={(e) => handleLineChange(line.id, 'description', e.target.value)}
                            placeholder="Description..."
                          />
                        </Table.Cell>
                        <Table.Cell>
                          <Input
                            size="sm"
                            type="number"
                            step="0.01"
                            min="0"
                            textAlign="right"
                            value={line.quantity || ''}
                            onChange={(e) => handleLineChange(line.id, 'quantity', e.target.value)}
                          />
                        </Table.Cell>
                        <Table.Cell>
                          <Input
                            size="sm"
                            type="number"
                            step="0.01"
                            min="0"
                            textAlign="right"
                            value={line.unit_price_ht || ''}
                            onChange={(e) => handleLineChange(line.id, 'unit_price_ht', e.target.value)}
                            placeholder="0.00"
                          />
                        </Table.Cell>
                        <Table.Cell>
                          <NativeSelectRoot size="sm">
                            <NativeSelectField
                              value={line.vat_rate}
                              onChange={(e) => handleLineChange(line.id, 'vat_rate', e.target.value)}
                            >
                              <option value="0">0%</option>
                              <option value="5.5">5.5%</option>
                              <option value="10">10%</option>
                              <option value="20">20%</option>
                            </NativeSelectField>
                          </NativeSelectRoot>
                        </Table.Cell>
                        <Table.Cell textAlign="right">
                          <Text fontWeight="medium" fontFamily="mono">
                            {formatCurrency(lineTtc)}
                          </Text>
                        </Table.Cell>
                        <Table.Cell>
                          <IconButton
                            variant="ghost"
                            size="xs"
                            colorPalette="red"
                            aria-label="Supprimer"
                            onClick={() => handleRemoveLine(line.id)}
                          >
                            <Trash2 size={14} />
                          </IconButton>
                        </Table.Cell>
                      </Table.Row>
                    )
                  })}

                  {/* Ligne d'ajout */}
                  <Table.Row>
                    <Table.Cell colSpan={7}>
                      <Button variant="ghost" size="xs" colorPalette="brand" onClick={handleAddLine}>
                        <Plus size={14} />
                        Ajouter une ligne
                      </Button>
                    </Table.Cell>
                  </Table.Row>

                  {/* Totaux */}
                  <Table.Row bg="gray.100">
                    <Table.Cell colSpan={5} textAlign="right" fontWeight="bold">
                      Total HT
                    </Table.Cell>
                    <Table.Cell textAlign="right" fontWeight="bold" fontFamily="mono">
                      {formatCurrency(totals.ht)}
                    </Table.Cell>
                    <Table.Cell></Table.Cell>
                  </Table.Row>
                  <Table.Row bg="gray.100">
                    <Table.Cell colSpan={5} textAlign="right" fontWeight="bold">
                      Total TVA
                    </Table.Cell>
                    <Table.Cell textAlign="right" fontWeight="bold" fontFamily="mono">
                      {formatCurrency(totals.vat)}
                    </Table.Cell>
                    <Table.Cell></Table.Cell>
                  </Table.Row>
                  <Table.Row bg="gray.200">
                    <Table.Cell colSpan={5} textAlign="right" fontWeight="bold">
                      Total TTC
                    </Table.Cell>
                    <Table.Cell textAlign="right" fontWeight="bold" fontFamily="mono" fontSize="md">
                      {formatCurrency(totals.ttc)}
                    </Table.Cell>
                    <Table.Cell></Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table.Root>
            </Box>

            {/* Notes */}
            <Field label="Notes internes">
              <Textarea
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="Notes internes..."
                rows={2}
              />
            </Field>

            {/* Indicateur de validation */}
            <Flex mt={4} justify="flex-end">
              {hasValidLines ? (
                <Badge colorPalette="green" size="lg">
                  <Check size={14} />
                  Facture prête
                </Badge>
              ) : (
                <Badge colorPalette="orange" size="lg">
                  <AlertCircle size={14} />
                  Saisissez au moins une ligne
                </Badge>
              )}
            </Flex>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={handleClose}>
              Annuler
            </Button>
            <Button
              colorPalette="brand"
              onClick={handleSubmit}
              loading={createInvoice.isPending}
              disabled={!formData.supplier_id || !formData.invoice_number.trim() || !hasValidLines}
            >
              Créer la facture
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  )
}
